import { useState, useEffect, useRef, useCallback } from 'react';
import { Chart, registerables } from "chart.js";
import { toast } from "react-toastify";
import ClientService from '../../../rest/clientService';
import FinancialService from '../../../rest/financialService';
import OrderService from '../../../rest/orderService';
import AssetsService from '../../../rest/assetsService';
import CustomerService from '../../../rest/customerService';
import { getLogin } from '../../../utils/StringUtils';
import { getToday } from '../../../utils/DateUtils';
import WebSocketManager from '../../../ws/client';

Chart.register(...registerables);

const getStartOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
};

export const useFinancialDashboard = () => {
    const [paymentStats, setPaymentStats] = useState({});
    const [productStats, setProductStats] = useState({});
    const [debtors, setDebtors] = useState([]);
    const [paymentsReceived, setPaymentsReceived] = useState([]);

    const [registryHistory, setRegistryHistory] = useState([]); 
    const [registryOrdersHistory, setRegistryOrdersHistory] = useState([]); 

    const [ordersInPayment, setOrdersInPayment] = useState([]);
    const [registryTransaction, setRegistryTransaction] = useState(null);

    const [loading, setLoading] = useState(false);

    const [chartFilters, setChartFilters] = useState({
        payment: { start: getStartOfMonth(), end: getToday() },
        product: { start: getStartOfMonth(), end: getToday() }
    });

    const [chartTitles, setChartTitles] = useState({ payment: '', product: '' });
    const [preferences, setPreferences] = useState({});
    const [enabledPayments, setEnabledPayments] = useState([]);

    const isSubmittingRef = useRef(false);

    const fetchPaymentChart = useCallback(async () => {
        const { start, end } = chartFilters.payment;
        if (!start || !end) return toast.warn("Selecione ambas as datas para filtrar.");

        try {
            const data = await FinancialService.getStatisticsOfPaymentMethods(start, end);
            setPaymentStats(data.data);
            setChartTitles(prev => ({ ...prev, payment: data.dateRange || `${start} até ${end}` }));
        } catch (error) {
            console.error(error);
            toast.error("Erro ao carregar gráfico de pagamentos.");
        }
    }, [chartFilters.payment]);

    const fetchProductChart = useCallback(async () => {
        const { start, end } = chartFilters.product;
        if (!start || !end) return toast.warn("Selecione ambas as datas para filtrar.");

        try {
            const data = await FinancialService.getTopSellProducts(start, end);
            setProductStats(data.data);
            setChartTitles(prev => ({ ...prev, product: data.dateRange || `${start} até ${end}` }));
        } catch (error) {
            console.error(error);
            toast.error("Erro ao carregar gráfico de produtos.");
        }
    }, [chartFilters.product]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                fetchPaymentChart();
                fetchProductChart();

                const prefs = await ClientService.getClientAdminPreferences();
                setPreferences(prefs || {});

                const paymentsList = prefs.accepted_payments
                    ?.split(";")
                    .map(v => v.trim().toUpperCase())
                    .filter(Boolean)
                    .filter(v => v !== "CREDITO FOODZ") || [];
                setEnabledPayments(paymentsList);

                const [debtorsData, paymentsData, registryData, registryOrdersData, ordersData] = await Promise.all([
                    FinancialService.getClientsWithOutstandingBalance(),
                    FinancialService.getPaymentsReceived(),
                    FinancialService.getRegistryHistory(),
                    FinancialService.getRegistryOrdersHistory(),
                    OrderService.getAllOrdersInPaymentProcess('true', 0, 10)
                ]);

                setDebtors(debtorsData || []);
                setPaymentsReceived(paymentsData || []);
                setRegistryHistory(registryData || []);
                setRegistryOrdersHistory(registryOrdersData || []);
                setOrdersInPayment(ordersData.content || []);

            } catch (error) {
                console.error("Erro ao carregar dashboard:", error);
                toast.error("Erro ao carregar dados financeiros.");
            }
        };
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOrdersInPayment = useCallback(async () => {
        try {
            const data = await OrderService.getAllOrdersInPaymentProcess('true', 0, 10);
            setOrdersInPayment(data.content || []);
        } catch (error) {
            console.error("Erro ao atualizar pedidos em pagamento:", error);
        }
    }, []);

    useEffect(() => {
        const clientCode = sessionStorage.getItem('clientCode');
        if (!clientCode) return;

        const topic = `/topic/clients/${clientCode}/orders`;
        const callback = (event) => {
            if (event.type === 'UPDATED' || event.type === 'CANCELED') {
                fetchOrdersInPayment();
            }
        };

        WebSocketManager.subscribe(topic, callback);
        return () => WebSocketManager.unsubscribe(topic);
    }, [fetchOrdersInPayment]);

    const actions = {
        notifyClient: async (client) => {
            if (isSubmittingRef.current) return;
            isSubmittingRef.current = true;
            setLoading(true);
            try {
                await FinancialService.notifyClientDebt(client.login, {
                    login: client.login,
                    credit: client.credit,
                    client_code: client.client_code
                });
                toast.success("Cobrança enviada com sucesso!");
                setDebtors(prev => prev.map(c => c.login === client.login ? { ...c, notified: true } : c));
            } catch (e) {
                toast.error("Erro ao enviar cobrança.");
            } finally {
                setLoading(false);
                isSubmittingRef.current = false;
            }
        },

        downloadReceipt: async (observationString) => {
            if (!observationString) return toast.error("Caminho do arquivo inválido.");
            try {
                const parts = observationString.split(' - ');
                if (parts.length < 3) throw new Error("Formato inválido");

                const { url, fileName } = await AssetsService.getPaymentsReceipt(parts[2].trim());
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                URL.revokeObjectURL(url);
            } catch (e) {
                toast.error("Erro ao baixar comprovante.");
            }
        },

        confirmReceivedPayment: async (payment) => {
            if (isSubmittingRef.current) return;
            isSubmittingRef.current = true;
            setLoading(true);
            try {
                const updatedCreditCharge = {
                    client_code: payment.client_code,
                    transactionDate: payment.transactionDate,
                    paymentMethod: payment.paymentMethod,
                    transaction_type: 'balance',
                    transactionId: payment.transactionId,
                    observations: payment.observations,
                    value: payment.value,
                    transactionSource: "MANUAL",
                };

                await CustomerService.updateStudentCredit(
                    getLogin(payment.customer_id),
                    {
                        login: getLogin(payment.customer_id),
                        credit: 0.00,
                        client_code: payment.client_code
                    },
                    updatedCreditCharge
                );

                toast.success("Pagamento confirmado!");
                setPaymentsReceived(prev => prev.filter(p => p.transactionId !== payment.transactionId));
            } catch (e) {
                toast.error("Erro ao confirmar pagamento.");
            } finally {
                setLoading(false);
                isSubmittingRef.current = false;
            }
        },

        removeOrderFromPaymentList: (orderId) => {
            setOrdersInPayment(prev => prev.filter(o => o.order_id !== orderId));
        },

        updateRegistryTransaction: (t) => setRegistryTransaction(t),

        updateChartDate: (chartType, field, value) => {
            setChartFilters(prev => ({
                ...prev,
                [chartType]: {
                    ...prev[chartType],
                    [field]: value
                }
            }));
        },

        searchPaymentChart: fetchPaymentChart,
        searchProductChart: fetchProductChart
    };

    return {
        data: {
            paymentStats, productStats, debtors, paymentsReceived,
            registryHistory, registryOrdersHistory, ordersInPayment,
            registryTransaction, chartTitles, chartFilters,
            loading, preferences, enabledPayments
        },
        actions
    };
};